import { useCallback, useEffect, useState } from "react";
import { X, ShieldCheck, AlertTriangle, Check, Loader2, Info } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import type { AdminModule } from "@/lib/adminModules";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface Props {
  adminId: string;
  adminName: string;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * Per-admin module access editor.
 *
 * Sends the full list rather than a delta, matching the API: with a delta,
 * whether a module ends up revoked depends on the order requests arrive in.
 */
export default function ModuleAccessModal({
  adminId,
  adminName,
  onClose,
  onSaved,
}: Props) {
  const [catalog, setCatalog] = useState<AdminModule[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [initial, setInitial] = useState<Set<string>>(new Set());
  const [configured, setConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [modulesResp, accessResp] = await Promise.all([
        api.get<any>("/admin/modules"),
        api.get<any>(`/admin/admins/${adminId}/modules`),
      ]);
      const modules: AdminModule[] =
        modulesResp?.modules ?? modulesResp?.data?.modules ?? [];
      const access = accessResp?.data ?? accessResp;

      setCatalog(modules);
      const granted = new Set<string>(access?.modules ?? []);
      setSelected(granted);
      setInitial(granted);
      setConfigured(!!access?.configured);
    } catch (e: any) {
      setError(e?.message || "Could not load permissions");
    } finally {
      setLoading(false);
    }
  }, [adminId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !saving) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const grantable = catalog.filter((m) => m.grantable);
  const fixed = catalog.filter((m) => !m.grantable);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });

  const grantedCount = grantable.filter((m) => selected.has(m.key)).length;
  const dirty =
    grantable.some((m) => selected.has(m.key) !== initial.has(m.key)) || !configured;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      // Only grantable keys go up — the fixed ones are not the caller's to set,
      // and the API rejects them rather than silently dropping them.
      const modules = grantable.filter((m) => selected.has(m.key)).map((m) => m.key);
      await api.put(`/admin/admins/${adminId}/modules`, { modules });
      onSaved?.();
      onClose();
    } catch (e: any) {
      setError(e?.message || "Could not save permissions");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="bg-white rounded-lg w-full max-w-lg max-h-[90vh] flex flex-col shadow-xl">
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <ShieldCheck className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Module Access</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Choose what {adminName} can open in the portal.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="py-10 flex justify-center">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              {!configured && (
                <div className="mb-4 rounded-md border border-blue-200 bg-blue-50 p-3 flex items-start gap-2">
                  <Info className="h-4 w-4 text-blue-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-blue-800">
                    This admin has never been configured, so they currently inherit
                    the default set shown below. Saving pins these choices.
                  </p>
                </div>
              )}

              <div className="space-y-1.5">
                {grantable.map((m) => {
                  const on = selected.has(m.key);
                  return (
                    <label
                      key={m.key}
                      className={cn(
                        "flex items-start gap-3 p-2.5 rounded-md border cursor-pointer transition-colors",
                        on
                          ? "border-primary/40 bg-primary/5"
                          : "border-gray-200 hover:bg-gray-50",
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => toggle(m.key)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900">
                          {m.label}
                        </span>
                        {m.description && (
                          <span className="block text-xs text-gray-500 mt-0.5">
                            {m.description}
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>

              {fixed.length > 0 && (
                <div className="mt-4 pt-3 border-t border-gray-100">
                  <p className="text-xs font-medium text-gray-700 mb-1.5">
                    Not configurable
                  </p>
                  <ul className="space-y-1">
                    {fixed.map((m) => (
                      <li key={m.key} className="text-xs text-gray-500">
                        <span className="font-medium text-gray-600">{m.label}</span>
                        {m.description ? ` — ${m.description}` : null}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {grantedCount === 0 && (
                <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-800">
                    With nothing selected this admin can still sign in, but will only
                    see the Dashboard.
                  </p>
                </div>
              )}

              {error && (
                <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200">
          <span className="text-xs text-gray-500">
            {grantedCount} of {grantable.length} modules
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={loading || saving || !dirty}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {saving ? "Saving…" : "Save access"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
